<template>
<section class="w-100 front-section">
  <div class="container-fluid hv-100">
    <global-message></global-message>
    <div class="row p-md-4 pb-md-0 p-3 pt-5" style="overflow-x:hidden; padding-top: 4rem !important;">
      <div class="border-around-rounded p-3 pb-md-3 pb-1" style="margin-top: 60px;">
        <FrontHeader></FrontHeader>

        <!-- ===== HERO SECTION ===== -->
        <div class="row align-items-center justify-content-center hero-section">
          <div class="col-lg-6 col-md-7 col-12 mt-5 pt-md-5">
            <div class="hero-badge mb-3">
              <span class="badge-text">🚀 Free for all creators</span>
            </div>
            <h1 class="hero-title">
              Monetize Your<br>
              <span class="hero-gradient-text">Audience</span>
            </h1>
            <p class="hero-subtitle mt-3 mb-4">
              Free tools to sell content, manage fans, accept custom requests, and grow your creator business — all in one platform.
            </p>
            <div class="d-flex flex-wrap gap-3 mt-4">
              <button class="btn stella-btn gradient-a px-4 py-2 rounded-pill font-family-poppins font-weight-600" @click="$router.push('/top-creators')">
                Explore Top Creators
              </button>
              <button class="btn btn-outline-light hero-secondary-btn px-4 py-2 rounded-pill font-family-poppins font-weight-600" @click="scrollToFeatures">
                See Features <span class="btn-arrow">↓</span>
              </button>
            </div>
          </div>
          <div class="col-lg-5 col-md-5 col-10 mt-5 mt-md-3 text-center">
            <img src="@/assets/images/stella/front/showcase-dashboard.png" class="img-fluid hero-showcase-img" alt="Stella Dashboard" width="500" height="313" fetchpriority="high" decoding="async">
          </div>
        </div>


      </div>

      <!-- ===== STATS STRIP ===== -->
      <div class="col-12 my-5 py-4">
        <div class="stats-strip custom-card-transparent">
          <div class="stat-item" v-for="(stat, i) in stats" :key="i">
            <div class="stat-value">{{ stat.value }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>

      <!-- ===== FEATURES GRID ===== -->
      <div class="col-12 my-4 py-4" id="features-section">
        <div class="text-center mb-5">
          <h2 class="section-title">Everything You Need to <span class="hero-gradient-text">Grow</span></h2>
          <p class="section-subtitle">Powerful tools built specifically for content creators</p>
        </div>
        <div class="container">
          <div class="row justify-content-center">
            <div class="col-lg-4 col-md-6 col-12 mb-4" v-for="(feature, i) in features" :key="i">
              <div class="feature-card custom-card-transparent">
                <div class="feature-icon">{{ feature.icon }}</div>
                <h5 class="feature-title">{{ feature.title }}</h5>
                <p class="feature-desc">{{ feature.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== PRODUCT SHOWCASES ===== -->
      <div class="col-12 my-5 py-5">
        <div class="container">

          <!-- Showcase 1: Messaging -->
          <div class="row align-items-center justify-content-center mb-5 py-4">
            <div class="col-md-5 col-10 mb-4 mb-md-0">
              <img src="@/assets/images/stella/front/showcase-messaging.png" class="img-fluid showcase-img" alt="Direct Messaging" width="500" height="313" loading="lazy" decoding="async">
            </div>
            <div class="col-md-5 col-12 ps-md-5">
              <div class="showcase-badge mb-2">💬 Direct Messaging</div>
              <h3 class="text-white font-family-poppins font-size-30 mb-3">Connect with Your Fans</h3>
              <p class="font-grey font-size-14 font-family-poppins">
                1-on-1 paid messaging with your subscribers. Set your own message prices, use filters to manage conversations, and build deeper relationships with your community.
              </p>
              <ul class="showcase-list">
                <li>Set custom message pricing</li>
                <li>Auto-filter unwanted messages</li>
                <li>Send media attachments</li>
              </ul>
            </div>
          </div>

          <!-- Showcase 2: Custom Requests -->
          <div class="row align-items-center justify-content-center mb-5 py-4 flex-md-row-reverse">
            <div class="col-md-5 col-10 mb-4 mb-md-0">
              <img src="@/assets/images/stella/front/showcase-storefront.png" class="img-fluid showcase-img" alt="Custom Requests" width="500" height="313" loading="lazy" decoding="async">
            </div>
            <div class="col-md-5 col-12 pe-md-5">
              <div class="showcase-badge mb-2">🎁 Custom Requests</div>
              <h3 class="text-white font-family-poppins font-size-30 mb-3">Sell Personalized Content</h3>
              <p class="font-grey font-size-14 font-family-poppins">
                Let your fans request personalized shoutouts, video calls, exclusive photo sets, and more. Set your own prices and fulfillment rules for each custom request type.
              </p>
              <ul class="showcase-list">
                <li>Unlimited custom request types</li>
                <li>Set your own prices</li>
                <li>Easy fulfillment workflow</li>
              </ul>
            </div>
          </div>

        </div>
      </div>

      <!-- ===== HOW IT WORKS ===== -->
      <div class="col-12 my-4 py-5">
        <div class="text-center mb-5">
          <h2 class="section-title">How It <span class="hero-gradient-text">Works</span></h2>
          <p class="section-subtitle">Get started in minutes, completely free</p>
        </div>
        <div class="container">
          <div class="row justify-content-center">
            <div class="col-md-4 col-12 mb-4 text-center" v-for="(step, i) in steps" :key="i">
              <div class="step-card custom-card-transparent">
                <div class="step-number">{{ i + 1 }}</div>
                <div class="step-icon">{{ step.icon }}</div>
                <h5 class="step-title">{{ step.title }}</h5>
                <p class="step-desc">{{ step.desc }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== TESTIMONIALS ===== -->
      <div class="testimonial-bg"></div>
      <div class="col-12 my-md-5 py-md-5">
        <div class="container">
          <div class="row justify-content-center">
            <div class="col-12 col-md-8">

              <div class="testimonial-carousel custom-card-transparent">
                <div class="testimonial-slide">
                  <div class="pt-3 px-4">
                    <div class="d-block text-center font-family-poppins font-size-18">
                      "{{ testimonials[activeTestimonial].quote }}"
                    </div>
                  </div>
                  <div class="d-block py-md-4 pt-4 pb-3 text-center">
                    <button style="width:100px;height:5px;padding:0;" class="btn btn-sm gradient-a px-3 font-weight-900 rounded-pill"></button>
                  </div>
                  <div class="d-flex justify-content-center align-items-center">
                    <div class="rounded-circle">
                      <img class="img-fluid outline-gradient-a rounded-circle p-1" :src="testimonials[activeTestimonial].avatar">
                    </div>
                    <div class="d-block text-start mx-2">
                      <div class="text-white font-size-16">{{ testimonials[activeTestimonial].name }}</div>
                      <p class="font-size-14 mb-0">{{ testimonials[activeTestimonial].subscribers }}</p>
                    </div>
                  </div>
                </div>

                <!-- Navigation arrows -->
                <div class="testimonial-nav">
                  <button class="testimonial-arrow" @click="prevTestimonial">‹</button>
                  <div class="testimonial-dots">
                    <span v-for="(t, i) in testimonials" :key="i" 
                          class="testimonial-dot" 
                          :class="{ active: i === activeTestimonial }" 
                          @click="activeTestimonial = i"></span>
                  </div>
                  <button class="testimonial-arrow" @click="nextTestimonial">›</button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- ===== CTA BANNER ===== -->
      <div class="col-12 my-5">
        <div class="cta-banner custom-card-transparent text-center">
          <h2 class="cta-title">Connect with <span class="hero-gradient-text">Top Talent</span></h2>
          <p class="cta-subtitle">Discover the most popular creators on Stella.</p>
          <button class="btn stella-btn gradient-a px-5 py-3 rounded-pill font-family-poppins font-weight-600 font-size-18 mt-3" @click="$router.push('/top-creators')">
            View Top Creators
          </button>
        </div>
      </div>

      <div class="py-5 invisible">&nbsp;</div>

    </div>
  </div>
  <FrontFooter></FrontFooter>
</section>
</template>

<script>

import FrontHeader from "@/components/module/front/FrontHeader";
import FrontFooter from "@/components/module/front/FrontFooter";
import GlobalMessage from "@/components/module/shared/GlobalMessage";
import {getTokenExp, isRole, isTokenExpired} from "@/common/http-common";
import {ACCESS_TOKEN} from "@/properties/close-friend";
import {httpMixin} from "@/mixin/http-mixin";
import {cleanUpAfterLogout} from "@/common/login-service";
import {mapActions} from "vuex";
import titleMixin from "@/mixin/title-mixin";
import {bonusPaymentPaid} from "@/common/analytics-service";
import testimonialAvatar from "@/assets/images/stella/front/testimonial-1.png";

export default {
  name: 'HomePage',
  components: {FrontFooter, FrontHeader, GlobalMessage},
  mixins: [httpMixin, titleMixin],
  title: 'Home',
  data() {
    return {
      activeTestimonial: 0,
      testimonialTimer: null,
      stats: [
        { value: '10,000+', label: 'Creators' },
        { value: '1M+', label: 'Transactions' },
        { value: '80%', label: 'Creator Earnings' },
        { value: '$0', label: 'Monthly Fee' }
      ],
      features: [
        { icon: '📸', title: 'IG Close Friends', desc: 'Manage your Instagram Close Friends list. Auto-add subscribers, track fan status, and manage access.' },
        { icon: '🎁', title: 'Custom Requests', desc: 'Let fans request personalized content. Set your own prices and fulfillment rules.' },
        { icon: '🛍️', title: 'Digital Storefront', desc: 'Sell exclusive photos, videos, and digital goods through your custom storefront.' },
        { icon: '💬', title: 'Direct Messaging', desc: '1-on-1 paid messaging with fans. Set message prices and use filters.' },
        { icon: '👥', title: 'Fan Management', desc: 'Track subscriber status, manage active and blocked fans, and view analytics.' },
        { icon: '💰', title: 'Smart Payouts', desc: 'Keep 80% of every transaction. No monthly fees. Automated Stripe payouts.' }
      ],
      steps: [
        { icon: '✨', title: 'Sign Up Free', desc: 'Create your Stella account in under 2 minutes. No credit card required.' },
        { icon: '🎨', title: 'Customize', desc: 'Set up your storefront, pricing, fan management rules, and branding.' },
        { icon: '💸', title: 'Start Earning', desc: 'Share your link, accept payments from day one, and watch your business grow.' }
      ],
      testimonials: [
        {
          quote: 'stella has everything I need as a full-time content creator, from file hosting to scheduled payouts. I love that stella prioritizes creators over anything else.',
          name: 'Yul.po',
          subscribers: '15.6k subscribers',
          avatar: testimonialAvatar
        },
        {
          quote: 'The custom request feature changed my business. I went from making a few hundred a month to a full-time income just by offering personalized content to my fans.',
          name: 'Maya J.',
          subscribers: '8.2k subscribers',
          avatar: testimonialAvatar
        },
        {
          quote: 'No other platform gives you this level of control over your community. The fan management tools and direct messaging are game changers.',
          name: 'Alex Rivera',
          subscribers: '22.1k subscribers',
          avatar: testimonialAvatar
        },
        {
          quote: 'I tried every creator platform out there. stella is the only one that actually feels like it was built FOR creators. The 80% payout rate is unbeatable.',
          name: 'Jenna K.',
          subscribers: '34.5k subscribers',
          avatar: testimonialAvatar
        },
        {
          quote: 'Setting up my digital storefront took less than 10 minutes. Within the first week I had already made more than my previous platform paid me in a month.',
          name: 'Marcus T.',
          subscribers: '11.3k subscribers',
          avatar: testimonialAvatar
        },
        {
          quote: 'The IG Close Friends integration is seamless. My subscribers get added automatically, and I can manage everything from one dashboard. So much time saved!',
          name: 'Sofia Chen',
          subscribers: '19.8k subscribers',
          avatar: testimonialAvatar
        },
        {
          quote: 'What I love most is the direct messaging feature. Being able to charge for DMs while having smart filters means I actually enjoy talking to my fans now.',
          name: 'Tyler Brooks',
          subscribers: '27.4k subscribers',
          avatar: testimonialAvatar
        }
      ]
    }
  },
  created() {
    console.log('HOME PAGE - CREATED');
    if (this.$route.query.source) {
      this.setSource(this.$route.query.source);
    } else {
      this.setSource(null);
    }

    let urlPrefix = this.$route.params.urlPrefix;
    if (urlPrefix) {
      console.log('urlPrefix', urlPrefix);
      this.$router.replace({name: 'Platform', params: {urlPrefix}});
    } else {
      console.log('HOMEPAGE - created');
      if (this.$route.query.chatMessage) {
        this.setRedirectTo({chatMessage: this.$route.query.chatMessage});
      }
      this.checkTokenValidity(() => {
        let tokenExp = getTokenExp(ACCESS_TOKEN);
        if (tokenExp && !isTokenExpired(ACCESS_TOKEN)) {
          if (isRole('INFLUENCER')) {
            this.$router.replace({name: 'CreatorDashboardMobile'});
          } else {
            this.$router.replace({name: 'CustomerRouter'});
          }
        }
      }, true);
    }
  },
  mounted() {
    this.testimonialTimer = setInterval(() => {
      this.activeTestimonial = (this.activeTestimonial + 1) % this.testimonials.length;
    }, 4000);
  },
  beforeDestroy() {
    if (this.testimonialTimer) {
      clearInterval(this.testimonialTimer);
    }
  },
  computed: {},
  methods: {
    ...mapActions(['setSource', 'setRedirectTo']),
    signUp() {
      this.$router.push({name: 'CreateAccountMobile'});
    },
    scrollToFeatures() {
      const el = document.getElementById('features-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    },
    nextTestimonial() {
      this.activeTestimonial = (this.activeTestimonial + 1) % this.testimonials.length;
      this.resetTimer();
    },
    prevTestimonial() {
      this.activeTestimonial = (this.activeTestimonial - 1 + this.testimonials.length) % this.testimonials.length;
      this.resetTimer();
    },
    resetTimer() {
      if (this.testimonialTimer) {
        clearInterval(this.testimonialTimer);
      }
      this.testimonialTimer = setInterval(() => {
        this.activeTestimonial = (this.activeTestimonial + 1) % this.testimonials.length;
      }, 4000);
    }
  }
};
</script>

<style scoped>
/* ===== GLOBAL WRAPPER ===== */
.front-section {
  background: transparent;
  min-height: 100vh;
}

/* ===== HERO ===== */
.hero-section {
  min-height: 80vh;
  padding-top: 40px;
}

.hero-badge {
  display: inline-block;
  padding: 6px 16px;
  border: 1px solid rgba(255, 89, 212, 0.3);
  border-radius: 20px;
  background: rgba(255, 89, 212, 0.08);
}
.badge-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  font-family: 'Poppins', sans-serif;
  letter-spacing: 0.5px;
}

.hero-title {
  font-family: 'Poppins', sans-serif;
  font-size: 64px;
  font-weight: 700;
  color: white;
  line-height: 1.1;
  letter-spacing: -1px;
}
@media (max-width: 768px) {
  .hero-title {
    font-size: 40px;
  }
}

.hero-gradient-text {
  background: linear-gradient(90deg, #DE8371, #C56FD1, #2F4FE9);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  line-height: 1.6;
  max-width: 480px;
}

.hero-showcase-img {
  width: 100%;
  max-width: 480px;
  height: auto;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  transition: transform 0.4s ease;
}
.hero-showcase-img:hover {
  transform: scale(1.02);
}

/* Hero Secondary Button & Micro-Animation */
.hero-secondary-btn {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #ffffff !important;
  font-weight: 600;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.hero-secondary-btn:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: rgba(229, 59, 185, 0.6) !important;
  color: #ffffff !important;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(229, 59, 185, 0.3), 0 0 15px rgba(255, 255, 255, 0.08) !important;
}

.hero-secondary-btn:active {
  transform: translateY(0) scale(0.97) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4) !important;
}

.btn-arrow {
  display: inline-block;
  margin-left: 4px;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.hero-secondary-btn:hover .btn-arrow {
  transform: translateY(3px);
}

/* ===== STATS STRIP ===== */
.stats-strip {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 32px 24px;
  backdrop-filter: blur(10px);
}
.stat-item {
  text-align: center;
  padding: 0 32px;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}
.stat-item:last-child {
  border-right: none;
}
.stat-value {
  font-family: 'Poppins', sans-serif;
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(90deg, #DE8371, #C56FD1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.stat-label {
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
@media (max-width: 768px) {
  .stat-item {
    padding: 12px 20px;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    width: 50%;
  }
  .stat-item:last-child,
  .stat-item:nth-child(even) {
    border-right: none;
  }
  .stat-value {
    font-size: 24px;
  }
}

/* ===== SECTION TITLES ===== */
.section-title {
  font-family: 'Poppins', sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: white;
}
.section-subtitle {
  color: rgba(255, 255, 255, 0.4);
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  margin-top: 8px;
}

/* ===== FEATURES GRID ===== */
.feature-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 32px 28px;
  height: 100%;
  transition: all 0.3s ease;
  cursor: default;
}
.feature-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 89, 212, 0.3);
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}
.feature-icon {
  font-size: 36px;
  margin-bottom: 16px;
}
.feature-title {
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin-bottom: 10px;
}
.feature-desc {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.6;
  margin-bottom: 0;
}

/* ===== PRODUCT SHOWCASES ===== */
.showcase-img {
  width: 100%;
  max-width: 480px;
  height: auto;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.4);
  transition: transform 0.4s ease;
}
.showcase-img:hover {
  transform: scale(1.03);
}
.showcase-badge {
  display: inline-block;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 89, 212, 0.1);
  border: 1px solid rgba(255, 89, 212, 0.2);
  padding: 4px 14px;
  border-radius: 20px;
  letter-spacing: 0.5px;
}
.showcase-list {
  list-style: none;
  padding: 0;
  margin-top: 16px;
}
.showcase-list li {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  padding: 6px 0;
  position: relative;
  padding-left: 20px;
}
.showcase-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  color: #C56FD1;
  font-weight: 700;
}

/* ===== HOW IT WORKS ===== */
.step-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 40px 28px;
  height: 100%;
  transition: all 0.3s ease;
  position: relative;
}
.step-card:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 89, 212, 0.2);
}
.step-number {
  font-family: 'Poppins', sans-serif;
  font-size: 64px;
  font-weight: 800;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), transparent);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: absolute;
  top: 10px;
  right: 20px;
}
.step-icon {
  font-size: 40px;
  margin-bottom: 16px;
}
.step-title {
  font-family: 'Poppins', sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: white;
  margin-bottom: 10px;
}
.step-desc {
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.6;
  margin-bottom: 0;
}

/* ===== CTA BANNER ===== */
.cta-banner {
  border-radius: 24px;
  padding: 64px 32px;
  position: relative;
  overflow: hidden;
  background: black;
}
.cta-title {
  font-family: 'Poppins', sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: white;
  position: relative;
  z-index: 1;
}
.cta-subtitle {
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 8px;
  position: relative;
  z-index: 1;
}

/* ===== CUSTOM TESTIMONIAL CAROUSEL ===== */
.testimonial-carousel {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 24px;
  padding: 48px 32px;
  position: relative;
  overflow: hidden;
  min-height: 350px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.testimonial-slide {
  animation: fadeIn 0.5s ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.testimonial-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 32px;
  gap: 16px;
}
.testimonial-arrow {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.testimonial-arrow:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}
.testimonial-dots {
  display: flex;
  gap: 8px;
}
.testimonial-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
}
.testimonial-dot.active {
  background: #C56FD1;
  width: 24px;
  border-radius: 4px;
}
</style>

<style>
body {
  background-color: #111111 !important;
}
</style>
